/******************************************************************
 *  AdminAnalytics.jsx — Platform Analytics
 ******************************************************************/
import React from "react";
import {
  Box,
  Typography,
  Stack,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Button,
  useTheme,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { Group, Apartment, TrendingUp } from "@mui/icons-material";
import SarIcon from "../../icons/SarIcon";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router-dom";

/* -------------------- shared style objects -------------------- */
const cardSx = {
  borderRadius: 1.5,
  boxShadow: (theme) => theme.palette.shadow.statCard,
  border: (theme) => `1px solid ${theme.palette.border.light}`,
  height: "100%",
};

/* -------------------- tiny helpers --------------------------- */
const MetricCard = ({ statKey, label, value, sub, icon }) => (
  <Card sx={cardSx} elevation={0}>
    <CardContent
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 3,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ color: "text.muted" }}>
          {label}
        </Typography>
        <Typography
          variant="h5"
          fontWeight={500}
          sx={{ color: "text.primary", mt: 0.5, letterSpacing: -0.5 }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            color: (theme) => theme.palette.success.main,
            fontSize: 12,
            fontWeight: 400,
          }}
        >
          {sub}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 1,
          bgcolor: (theme) => theme.palette.statIcon[statKey].bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    </CardContent>
  </Card>
);

const Gauge = ({ label, score, color }) => {
  const numericScore = parseFloat(score);
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={0.75}
      >
        <Typography variant="body2" sx={{ color: "text.neutral", fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color }}
        >
          {score}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={numericScore}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: "border.light",
          "& .MuiLinearProgress-bar": {
            bgcolor: color,
            borderRadius: 999,
          },
        }}
      />
    </Box>
  );
};

/* ------------------------------------------------------------- */
export default function AdminAnalytics() {
  const navigate = useNavigate();
  const theme = useTheme();
  const days = ["Nov 1", "Nov 2", "Nov 3", "Nov 4", "Nov 5", "Nov 6", "Nov 7"];
  const active = [45, 52, 48, 60, 58, 65, 72];
  const neu = [20, 25, 22, 30, 28, 35, 40];

  const activeUsersColor = theme.palette.accent.teal.chipText;
  const newUsersColor = theme.palette.statIcon.users.color;

  const kpis = [
    {
      key: "users",
      statKey: "users",
      label: "User Growth",
      value: "+12.5%",
      sub: "Month over month",
      icon: (
        <Group
          sx={{ color: (theme) => theme.palette.brand.cyan }}
        />
      ),
    },
    {
      key: "assets",
      statKey: "assets",
      label: "Asset Listings",
      value: "+8.3%",
      sub: "New listings growth",
      icon: (
        <Apartment
          sx={{ color: (theme) => theme.palette.statIcon.assets.color }}
        />
      ),
    },
    {
      key: "volume",
      statKey: "volume",
      label: "Transaction Volume",
      value: "+18.2%",
      sub: "Compared to last month",
      icon: (
        <TrendingUp
          sx={{ color: (theme) => theme.palette.statIcon.volume.color }}
        />
      ),
    },
    {
      key: "revenue",
      statKey: "revenue",
      label: "Revenue Growth",
      value: "+24.5%",
      sub: "Platform fees collected",
      icon: (
        <SarIcon sx={{ color: (theme) => theme.palette.statIcon.revenue.color }} />
      ),
    },
  ];

  return (
    <Box>
      {/* Back link */}
      <Button
        onClick={() => navigate("/admin")}
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: "none",
          color: "text.primary",
          mb: 2,
          px: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        Back to Dashboard
      </Button>

      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: "text.primary", letterSpacing: -0.5 }}
        >
          Platform Analytics
        </Typography>
        <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
          Detailed insights and performance metrics
        </Typography>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {kpis.map((m) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={m.key}>
            <MetricCard {...m} />
          </Grid>
        ))}
      </Grid>

      {/* Chart + gauges */}
      <Grid container spacing={3}>
        {/* User Engagement chart */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={cardSx} elevation={0}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: "text.primary" }}
              >
                User Engagement
              </Typography>
              <Typography variant="body2" sx={{ color: "text.muted", mb: 2 }}>
                Daily active users trend
              </Typography>

              <Box sx={{ width: "100%", height: 280 }}>
                <LineChart
                  xAxis={[{ scaleType: "band", data: days }]}
                  series={[
                    {
                      data: active,
                      label: "Active Users",
                      color: activeUsersColor,
                      area: true,
                      showMark: false,
                      curve: "monotoneX",
                    },
                    {
                      data: neu,
                      label: "New Users",
                      color: newUsersColor,
                      area: false,
                      showMark: false,
                      curve: "monotoneX",
                    },
                  ]}
                  margin={{ top: 10, bottom: 40, left: 40, right: 10 }}
                  slotProps={{
                    legend: {
                      direction: "row",
                      position: { vertical: "bottom", horizontal: "middle" },
                      itemMarkWidth: 10,
                      itemMarkHeight: 10,
                      labelStyle: { fontSize: 12 },
                    },
                  }}
                  sx={{
                    "& .MuiAreaElement-series-auto-generated-id-0": {
                      fillOpacity: 0.2,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Platform Health gauges */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={cardSx} elevation={0}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: "text.primary" }}
              >
                Platform Health
              </Typography>
              <Typography variant="body2" sx={{ color: "text.muted", mb: 3 }}>
                Key performance indicators
              </Typography>

              <Stack spacing={3}>
                <Gauge
                  label="User Engagement"
                  score="94%"
                  color={(theme) => theme.palette.stats.green}
                />
                <Gauge
                  label="Asset Approval Rate"
                  score="87%"
                  color={(theme) => theme.palette.statIcon.users.color}
                />
                <Gauge
                  label="KYC Completion"
                  score="91%"
                  color={(theme) => theme.palette.statIcon.revenue.color}
                />
                <Gauge
                  label="Transaction Success"
                  score="98%"
                  color={(theme) => theme.palette.stats.purple}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
